/**
 * Shared Relewise recommendation batcher — window.spejder.relewiseBatch.
 * Loaded by snippets/relewise-config.liquid right after the SDK, so every
 * Relewise-powered row on a page can hand its request here instead of calling
 * the Recommender itself.
 *
 * Why: a page renders several rows at once (homepage tab rows, the PDP's
 * "Andre kunder købte også" + "Du har kigget på" + any placed recommendation
 * section), and each used to fire its own request. Relewise's go-live check
 * (2026-09-15) asked for the rows loaded together to be ONE
 * ProductRecommendationRequestCollection — fewer round trips, and with
 * `requireDistinctProductsAcrossResults` Relewise itself makes sure two rows
 * never show the same product.
 *
 * How: recommend(request) queues the built request and answers a promise for
 * its own response. The first request opens a short window; everything queued
 * before it closes goes out as a single batchProductRecommendations call and
 * the responses are handed back index-aligned. A row that resolves later (the
 * cart drawer on a cart change, a section added in the editor) simply opens a
 * new window — a batch of one is still one request.
 *
 * Only recommendation requests batch here (Popular, Personal, PurchasedWith*,
 * SimilarProducts, ViewedAfterViewing, RecentlyViewed, SortProducts). Search
 * requests are a different collection (Searcher.batch) and stay where they are.
 *
 * Nothing here is fatal: a failed batch rejects every caller's promise, and
 * each caller already falls back to its Shopify baseline on rejection.
 */
!(function () {
  'use strict';

  window.spejder = window.spejder || {};
  if (window.spejder.relewiseBatch) return;

  /* ---- Consent-correct user: window.spejder.relewiseUser() ----------------
     The Relewise app embed exposes the consent-aware identity as
     ShopifyRelewise.getUserAsync(), but it assigns that global inside an async
     init that runs AFTER the theme's deferred scripts. Every row used to test
     for the global synchronously at script time, find nothing, and fall back
     to an anonymous user — so no recommendation was ever personalised, even
     with marketing consent given (found while simulating the cookie banner,
     2026-09-16). This waits for the global (bounded), then asks the app.

     The app answers with the visitor it worked out when the page loaded: it
     resolves the user ONCE per page, so after "Accepter alle" (or a
     withdrawal) getUserAsync() keeps returning the old identity until the
     next page (Relewise go-live check 2026-10-06, reproduced on live
     2026-10-07). followConsent() re-checks Shopify's Customer Privacy API on
     every call, with the embed's own rule for that provider (marketing
     allowed → temporaryId = consentId(), else anonymous), so search,
     recommendations and variant tracking follow the banner at once, both
     ways. The app's own tracking (page views, cart) switches on the next
     page. If the embed's cookie-consent provider is ever changed away from
     "Shopify Customer Privacy API", this rule must change with it. */
  var APP_WAIT_MS = 4000;
  var appReady = null;

  function anonymous() { return Relewise.UserFactory.anonymous(); }

  function followConsent(user) {
    try {
      var privacy = window.Shopify && Shopify.customerPrivacy;
      if (!user || !privacy || typeof privacy.marketingAllowed !== 'function') return user;
      var allowed = !!privacy.marketingAllowed();
      if (allowed === !!user.temporaryId) return user;
      /* Withdrawn: no identifiers at all, as the app sends without consent. */
      if (!allowed) return Relewise.UserFactory.anonymous({ classifications: user.classifications });
      /* Given: the id the app sends from the next page on. A logged-in
         customer's authenticatedId is added by the app on that next page. */
      var id = typeof privacy.consentId === 'function' ? privacy.consentId() : '';
      return id ? Relewise.UserFactory.byTemporaryId(id, { classifications: user.classifications }) : user;
    } catch (error) {
      return user;
    }
  }

  function waitForApp() {
    if (appReady) return appReady;
    var deadline = Date.now() + APP_WAIT_MS;
    appReady = new Promise(function (resolve) {
      (function poll() {
        if (window.ShopifyRelewise && typeof ShopifyRelewise.getUserAsync === 'function') return resolve(true);
        if (Date.now() > deadline) return resolve(false);
        setTimeout(poll, 50);
      })();
    });
    return appReady;
  }

  window.spejder.relewiseUser = function () {
    if (!window.Relewise) return Promise.reject(new Error('Relewise SDK missing'));
    return waitForApp()
      .then(function (found) {
        return found ? ShopifyRelewise.getUserAsync().catch(anonymous) : anonymous();
      })
      .then(followConsent)
      .catch(anonymous);
  };

  /* ---- Per-term search limit: window.spejder.relewiseSearchAllowed(term) --
     Client rule (Trello "Site Search limit på term", 2026-10-14): one visitor
     may send the same search term to Relewise at most 100 times a minute. The
     search page and the overlay ask right before each product search and skip
     Relewise on a no (the overlay falls back to Shopify, the search page shows
     its empty state). It only covers searches our own pages send: the October
     2026 flood of one term came straight at Relewise's API and never ran this.

     The count lives in localStorage, so a visitor's tabs share one budget and a
     reload does not reset it, and nothing in it outlives the minute. Terms are
     stored as hashes, never as typed. With storage blocked it counts in
     memory, per page. */
  var SEARCH_LIMIT = 100;
  var SEARCH_WINDOW_MS = 60 * 1000;
  var SEARCH_LOG_KEY = 'spejder:relewise-search-log';
  var searchLogInMemory = null; // set once localStorage turns out unusable

  function searchTermKey(term) {
    // "Spejdermærke  Bangebukse" and "spejdermærke bangebukse" are one term.
    var text = String(term || '').trim().toLowerCase().replace(/\s+/g, ' ');
    var hash = 0x811c9dc5; // FNV-1a
    for (var i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }

  function readSearchLog() {
    if (searchLogInMemory) return searchLogInMemory;
    var raw;
    try {
      raw = window.localStorage.getItem(SEARCH_LOG_KEY);
    } catch (error) {
      return (searchLogInMemory = {});
    }
    try {
      var log = JSON.parse(raw || '{}');
      return log && typeof log === 'object' ? log : {};
    } catch (error) {
      return {}; // unreadable: start over, the next write replaces it
    }
  }

  function writeSearchLog(log) {
    if (searchLogInMemory) {
      searchLogInMemory = log;
      return;
    }
    try {
      window.localStorage.setItem(SEARCH_LOG_KEY, JSON.stringify(log));
    } catch (error) {
      searchLogInMemory = log;
    }
  }

  /** @returns {boolean} true (and counted) when the search may go to Relewise. */
  window.spejder.relewiseSearchAllowed = function (term) {
    var now = Date.now();
    var log = readSearchLog();
    var recent = {};
    Object.keys(log).forEach(function (key) {
      var times = (Array.isArray(log[key]) ? log[key] : []).filter(function (time) { return now - time < SEARCH_WINDOW_MS; });
      if (times.length) recent[key] = times;
    });
    var key = searchTermKey(term);
    var allowed = (recent[key] || []).length < SEARCH_LIMIT;
    if (allowed) recent[key] = (recent[key] || []).concat(now);
    writeSearchLog(recent);
    return allowed;
  };

  /* Rows queue their request as soon as the app embed hands them the consent
     user, which happens for all of them within the same tick; the window only
     has to outlast that, not a network round trip. */
  var WINDOW_MS = 50;

  var queue = [];
  var timer = null;

  function flush() {
    timer = null;
    var batch = queue;
    queue = [];
    if (!batch.length) return;

    var cfg = window.spejder.relewise;
    if (!cfg || !cfg.enabled || !window.Relewise || !Relewise.ProductsRecommendationCollectionBuilder) {
      batch.forEach(function (q) { q.reject(new Error('Relewise unavailable')); });
      return;
    }

    /* Low-priority requests go last. Relewise hands out distinct products in
       request order, so a row nobody sees yet (the cart drawer's page-load
       prefetch) only gets what the visible rows left over. */
    batch = batch.filter(function (q) { return !q.low; }).concat(batch.filter(function (q) { return q.low; }));

    var builder = new Relewise.ProductsRecommendationCollectionBuilder().requireDistinctProductsAcrossResults(true);
    batch.forEach(function (q) { builder.addRequest(q.request); });

    new Relewise.Recommender(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl })
      .batchProductRecommendations(builder.build())
      .then(function (result) {
        var responses = (result && result.responses) || [];
        batch.forEach(function (q, i) { q.resolve(responses[i] || null); });
      })
      .catch(function (error) {
        batch.forEach(function (q) { q.reject(error); });
      });
  }

  window.spejder.relewiseBatch = {
    /**
     * @param {object} request a built ProductRecommendationRequest
     * @param {object} [options] `{ priority: 'low' }` for a row that is not on
     *   screen yet: it is sent after the others in the same batch.
     * @returns {Promise<object|null>} that request's own response
     *   ({ recommendations: [...] }) — the same shape the single-request
     *   Recommender methods resolve with.
     */
    recommend: function (request, options) {
      var low = !!(options && options.priority === 'low');
      return new Promise(function (resolve, reject) {
        queue.push({ request: request, resolve: resolve, reject: reject, low: low });
        if (!timer) timer = setTimeout(flush, WINDOW_MS);
      });
    },
  };
})();
